import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Fehlender oder ungültiger Token-Link.");
      return;
    }
    if (password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-md bg-white border border-slate-200 p-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-overline text-slate-500 hover:text-slate-900 mb-6"
        >
          <ArrowLeft size={12} strokeWidth={1.5} />
          Zurück zur Anmeldung
        </Link>

        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Neues Passwort</div>
        <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900 mt-2">
          Passwort zurücksetzen.
        </h1>

        {done ? (
          <div className="mt-8 flex items-start gap-3 border-l-2 border-emerald-600 pl-4 py-2" data-testid="reset-success">
            <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">
              Passwort erfolgreich aktualisiert. Du wirst zur Anmeldung weitergeleitet…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="reset-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                Neues Passwort
              </label>
              <input
                data-testid="reset-password-input"
                type="password"
                className="input-tech"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                Passwort bestätigen
              </label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                className="input-tech"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            {error && (
              <div data-testid="reset-error" className="text-sm text-red-600 border-l-2 border-red-600 pl-3 py-1">
                {error}
              </div>
            )}
            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-4 text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Wird gespeichert…" : "Passwort aktualisieren"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
